import { createAsyncThunk } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';
import { LogType, Logger } from '@hitiq/logger';
import { take } from 'rxjs';
import { queryParamsSelector } from '../../queryParams/global.query.slice';
import { AmplifyDataStore } from '../../dataStorePersistence/AmplifyDataStore';

// This type describes the error object structure:
type LogoutError = {
  message: string;
};

type LogoutThunkPayload = {
  amplifyDataStore: AmplifyDataStore<any>;
  logger: Logger;
};

export const logoutThunk = createAsyncThunk<
  undefined,
  LogoutThunkPayload,
  { rejectValue: LogoutError }
>(
  'auth/logoutThunk',
  async (
    payload: LogoutThunkPayload,
    thunkApi,
    // TODO: figure out return type of rejectWithValue something like RejectWithValue<Alert>
  ) => {
    const { amplifyDataStore, logger } = payload;

    try {
      await clearDataOnceOutboxIsEmpty(amplifyDataStore, logger, thunkApi);
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: 'Clear datastore failed.',
      });
    }

    try {
      logger.log(LogType.debug, `Datastore: Auth.signOut() called`);
      return await Auth.signOut();
    } catch (error) {
      logger.log(LogType.debug, `Datastore: Auth.signOut() error: ${error}`);
      return thunkApi.rejectWithValue({
        message: 'Logout failed.',
      });
    }
  },
);
const clearDataOnceOutboxIsEmpty = async (
  amplifyDataStore: AmplifyDataStore<any>,
  logger: Logger,
  thunkApi: any,
) => {
  return new Promise((resolve, reject) => {
    const sub = amplifyDataStore.outboxIsEmpty.subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (isEmpty: boolean) => {
        logger.log(
          LogType.debug,
          `Datastore: outboxIsEmpty isEmpty === ${isEmpty}`,
        );
        if (!isEmpty) {
          // TODO: alert that data might be lost if they logout before data has synced
          return;
        }

        // clear all dataStore data (should we do this or can we just getAll with accountId? Keeping all data in datastore and only loading data with correct accountId could cause security issues if people are sharing devices. But seems unlikely that they will share device. More likely that one person has two accounts e.g. guardian and org in which case it is not a problem to keep data if we only load data with owned by accountId)
        const res = await amplifyDataStore.clear();
        if (!res.success) {
          // QUESTION: what should we do if datastore fail to clear? Retry, let user know or ?
          logger.log(
            LogType.debug,
            `Datastore: amplifyDataStore.clear() !res.success in logout.thunk after outboxIsEmpty`,
          );
          reject();
        }

        // TODO: DON'T IMPORT SLICE DIRECTLY (probably. maybe it is ok because it is global but it breaks the pattern of declaring all dependencies in services)
        const { email } = queryParamsSelector(thunkApi.getState());

        // clear all redux data
        thunkApi.dispatch({
          type: 'RESET_STORE',
        });
        logger.log(LogType.debug, `Datastore: cleared all redux data`);

        if (email) {
          // reset last logged in email for dev login
          thunkApi.dispatch({
            type: 'globalQuerySlice/dispatchGlobalQuery',
            payload: {
              email,
            },
          });
        }

        resolve(true);

        sub?.unsubscribe();
      },
    );
  });
};
